import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select as MuiSelect,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  InputAdornment,
  LinearProgress,
  Card,
  CardContent,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import HiveIcon from '@mui/icons-material/Hive'
import SearchIcon from '@mui/icons-material/Search'
import InventoryIcon from '@mui/icons-material/Inventory2'
import LocalDrinkIcon from '@mui/icons-material/LocalDrink'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import BuildIcon from '@mui/icons-material/Build'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { getInventory, getYards, apiClient } from '../services/api'

const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin: 0;
`

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`

const FilterBar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

interface InventoryItem {
  _id?: string
  id?: string
  type: 'honey' | 'boxes' | 'tools' | string
  quantity: number
  warehouseId?: number | string
  yardId?: number | string
  format?: 'barrel' | 'combHoney' | string
  boxType?: 'deeps' | 'super' | 'shallow' | string
  description?: string
  reorderLevel?: number
}

interface Yard {
  id?: number | string
  _id?: string
  name: string
  address?: string
}

type SortKey = 'type' | 'quantity' | 'location' | 'status'
type SortDir = 'asc' | 'desc'

const REORDER_THRESHOLD = 10

function InventoryScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const [inventory, setInventory] = useState<InventoryItem[]>([])
  const [yards, setYards] = useState<Yard[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('all')
  const [search, setSearch] = useState('')
  const [locationFilter, setLocationFilter] = useState('all')
  const [sortKey, setSortKey] = useState<SortKey>('type')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    type: 'boxes',
    quantity: 0,
    boxType: '',
    format: '',
    description: '',
    warehouseId: '',
    yardId: '',
  })
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [transferItem, setTransferItem] = useState<InventoryItem | null>(null)
  const [transferForm, setTransferForm] = useState({ quantity: 0, destination: '' })
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  })

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [invRes, yardsRes] = await Promise.all([getInventory(), getYards()])
      setInventory(invRes.data || [])
      setYards(yardsRes.data || [])
    } catch (error) {
      console.error('Error loading inventory:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  // Helpers
  function getItemCategory(item: InventoryItem): string {
    const desc = (item.description || '').toLowerCase()
    if (item.type === 'boxes') return 'boxes'
    if (item.type === 'honey' && item.format === 'combHoney') return 'combs'
    if (item.type === 'honey') return 'honey'
    if (item.type === 'tools' && (desc.includes('sugar') || desc.includes('syrup'))) return 'supplies'
    if (item.type === 'tools') return 'equipment'
    if (item.type === 'swarms') return 'swarms'
    if (item.type === 'queens') return 'queens'
    return item.type
  }

  function getCategoryLabel(cat: string): string {
    const map: Record<string, string> = {
      boxes: t('inventory.boxes'),
      honey: t('inventory.honey'),
      combs: t('inventory.combs'),
      // Legacy "tools" items: no longer offered as a category, but existing rows still render with this label
      equipment: t('inventory.tools'),
      supplies: t('inventory.supplies'),
      swarms: t('inventory.swarms'),
      queens: t('inventory.queens'),
    }
    return map[cat] || cat
  }

  function getCategoryIcon(cat: string) {
    switch (cat) {
      case 'boxes': return <ViewInArIcon />
      case 'honey': return <LocalDrinkIcon />
      case 'combs': return <HiveIcon />
      case 'equipment': return <BuildIcon />
      case 'supplies': return <InventoryIcon />
      case 'swarms': return <InventoryIcon />
      case 'queens': return <InventoryIcon />
      default: return <InventoryIcon />
    }
  }

  function getItemName(item: InventoryItem): string {
    const cat = getItemCategory(item)
    if (cat === 'boxes' && item.boxType) {
      return t(`inventory.boxTypes.${item.boxType}`, { defaultValue: item.boxType })
    }
    if (cat === 'honey' && item.format) {
      const formatLabel = item.format === 'barrel' ? t('inventory.barrel') : t('inventory.combHoney')
      return formatLabel
    }
    if (item.description) return item.description
    return getCategoryLabel(cat)
  }

  function getLocationName(item: InventoryItem): string {
    if (item.warehouseId) {
      return t('inventory.warehouseNumber', { id: item.warehouseId })
    }
    if (item.yardId) {
      const yard = yards.find(y => String(y.id || y._id) === String(item.yardId))
      return yard?.name || t('inventory.yardNumber', { id: item.yardId })
    }
    return '-'
  }

  function getLocationType(item: InventoryItem): 'warehouse' | 'yard' | 'none' {
    if (item.warehouseId) return 'warehouse'
    if (item.yardId) return 'yard'
    return 'none'
  }

  function getStockStatus(item: InventoryItem): 'ok' | 'low' | 'critical' {
    const threshold = item.reorderLevel || REORDER_THRESHOLD
    if (item.quantity <= 0) return 'critical'
    if (item.quantity <= threshold) return 'low'
    return 'ok'
  }

  // Locations for filter
  const locations = useMemo(() => {
    const locs: { id: string; name: string; type: 'warehouse' | 'yard' }[] = []
    const warehouseIds = new Set<string>()
    const yardIds = new Set<string>()

    inventory.forEach(item => {
      if (item.warehouseId && !warehouseIds.has(String(item.warehouseId))) {
        warehouseIds.add(String(item.warehouseId))
        locs.push({ id: `w-${item.warehouseId}`, name: t('inventory.warehouseNumber', { id: item.warehouseId }), type: 'warehouse' })
      }
      if (item.yardId && !yardIds.has(String(item.yardId))) {
        yardIds.add(String(item.yardId))
        const yard = yards.find(y => String(y.id || y._id) === String(item.yardId))
        locs.push({ id: `y-${item.yardId}`, name: yard?.name || t('inventory.yardNumber', { id: item.yardId }), type: 'yard' })
      }
    })
    return locs
  }, [inventory, yards, t])

  // Filtering + sorting
  const filteredItems = useMemo(() => {
    let items = [...inventory]

    // Category tab
    if (tab !== 'all') {
      items = items.filter(item => getItemCategory(item) === tab)
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(item =>
        getItemName(item).toLowerCase().includes(q) ||
        getLocationName(item).toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q)
      )
    }

    // Location
    if (locationFilter !== 'all') {
      if (locationFilter.startsWith('w-')) {
        const wId = locationFilter.slice(2)
        items = items.filter(item => String(item.warehouseId) === wId)
      } else if (locationFilter.startsWith('y-')) {
        const yId = locationFilter.slice(2)
        items = items.filter(item => String(item.yardId) === yId)
      }
    }

    // Sort
    items.sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'type':
          cmp = getItemName(a).localeCompare(getItemName(b))
          break
        case 'quantity':
          cmp = a.quantity - b.quantity
          break
        case 'location':
          cmp = getLocationName(a).localeCompare(getLocationName(b))
          break
        case 'status': {
          const order = { critical: 0, low: 1, ok: 2 }
          cmp = order[getStockStatus(a)] - order[getStockStatus(b)]
          break
        }
      }
      return sortDir === 'asc' ? cmp : -cmp
    })

    return items
  }, [inventory, tab, search, locationFilter, sortKey, sortDir, yards, t])

  // KPI calculations
  const kpis = useMemo(() => {
    const totalItems = inventory.length
    const totalUnits = inventory.reduce((sum, item) => sum + (item.quantity || 0), 0)
    const lowStock = inventory.filter(item => getStockStatus(item) === 'low').length
    const critical = inventory.filter(item => getStockStatus(item) === 'critical').length
    const categories = new Set(inventory.map(getItemCategory)).size
    return { totalItems, totalUnits, lowStock, critical, categories }
  }, [inventory])

  // Category counts for tabs
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: inventory.length }
    inventory.forEach(item => {
      const cat = getItemCategory(item)
      counts[cat] = (counts[cat] || 0) + 1
    })
    return counts
  }, [inventory])

  // Sort handler
  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  // Dialog handlers
  function handleAddClick() {
    setEditingItem(null)
    setForm({ type: 'boxes', quantity: 0, boxType: '', format: '', description: '', warehouseId: '', yardId: '' })
    setDialogOpen(true)
  }

  function handleEditClick(item: InventoryItem) {
    setEditingItem(item)
    setForm({
      type: item.type,
      quantity: item.quantity,
      boxType: item.boxType || '',
      format: item.format || '',
      description: item.description || '',
      warehouseId: item.warehouseId ? String(item.warehouseId) : '',
      yardId: item.yardId ? String(item.yardId) : '',
    })
    setDialogOpen(true)
  }

  function handleDeleteClick(item: InventoryItem) {
    setEditingItem(item)
    setDeleteDialogOpen(true)
  }

  async function handleSave() {
    setSaving(true)
    try {
      const payload = {
        type: form.type,
        quantity: Number(form.quantity),
        boxType: form.type === 'boxes' ? form.boxType : undefined,
        format: form.type === 'honey' ? form.format : undefined,
        description: form.description || undefined,
        warehouseId: form.warehouseId || undefined,
        yardId: form.yardId || undefined,
      }

      if (editingItem) {
        const id = editingItem._id || editingItem.id
        await apiClient.put(`/inventory/${id}`, payload)
        setSnackbar({ open: true, message: t('inventory.updateSuccess'), severity: 'success' })
      } else {
        await apiClient.post('/inventory', payload)
        setSnackbar({ open: true, message: t('inventory.addSuccess'), severity: 'success' })
      }
      setDialogOpen(false)
      await loadData()
    } catch (error) {
      console.error('Error saving inventory:', error)
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!editingItem) return
    setSaving(true)
    try {
      const id = editingItem._id || editingItem.id
      await apiClient.delete(`/inventory/${id}`)
      setDeleteDialogOpen(false)
      setSnackbar({ open: true, message: t('inventory.deleteSuccess'), severity: 'success' })
      await loadData()
    } catch (error) {
      console.error('Error deleting inventory:', error)
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  function handleTransferClick(item: InventoryItem) {
    setTransferItem(item)
    setTransferForm({ quantity: 1, destination: '' })
    setTransferDialogOpen(true)
  }

  async function handleTransfer() {
    if (!transferItem) return
    setSaving(true)
    try {
      const id = transferItem._id || transferItem.id
      const payload: any = { quantity: Number(transferForm.quantity) }
      if (transferForm.destination.startsWith('w-')) {
        payload.destinationWarehouseId = Number(transferForm.destination.slice(2))
      } else if (transferForm.destination.startsWith('y-')) {
        payload.destinationYardId = transferForm.destination.slice(2)
      }
      await apiClient.post(`/inventory/${id}/transfer`, payload)
      setTransferDialogOpen(false)
      setSnackbar({ open: true, message: t('inventory.transferSuccess'), severity: 'success' })
      await loadData()
    } catch (error: any) {
      const msg = error?.response?.data?.error || t('common.error')
      setSnackbar({ open: true, message: msg, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // Build all possible destinations (warehouses + yards) for transfer dialog
  const allDestinations = useMemo(() => {
    const dests: { id: string; name: string; type: 'warehouse' | 'yard' }[] = []
    const warehouseIds = new Set<string>()

    // Get warehouses from inventory data
    inventory.forEach(item => {
      if (item.warehouseId && !warehouseIds.has(String(item.warehouseId))) {
        warehouseIds.add(String(item.warehouseId))
        dests.push({ id: `w-${item.warehouseId}`, name: t('inventory.warehouseNumber', { id: item.warehouseId }), type: 'warehouse' })
      }
    })

    // Add all yards
    yards.forEach(y => {
      const yId = String(y.id || y._id)
      dests.push({ id: `y-${yId}`, name: y.name, type: 'yard' })
    })

    return dests
  }, [inventory, yards, t])

  function renderStatusChip(item: InventoryItem) {
    const status = getStockStatus(item)
    if (status === 'critical') {
      return <Chip icon={<WarningAmberIcon />} label={t('inventory.status.critical')} color="error" size="small" />
    }
    if (status === 'low') {
      return <Chip icon={<WarningAmberIcon />} label={t('inventory.status.low')} color="warning" size="small" />
    }
    return <Chip icon={<CheckCircleIcon />} label={t('inventory.status.ok')} color="success" size="small" variant="outlined" />
  }

  function renderLocationChip(item: InventoryItem) {
    const locType = getLocationType(item)
    if (locType === 'warehouse') {
      return (
        <Chip
          icon={<WarehouseIcon />}
          label={getLocationName(item)}
          size="small"
          variant="outlined"
          sx={{ borderColor: '#7c4dff', color: '#7c4dff' }}
        />
      )
    }
    if (locType === 'yard') {
      return (
        <Chip
          icon={<HiveIcon />}
          label={getLocationName(item)}
          size="small"
          variant="outlined"
          sx={{ borderColor: '#ff9800', color: '#ff9800' }}
        />
      )
    }
    return <Typography variant="body2" color="text.secondary">-</Typography>
  }

  function renderCategoryChip(item: InventoryItem) {
    const cat = getItemCategory(item)
    const colorMap: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
      boxes: 'info',
      honey: 'warning',
      combs: 'secondary',
      equipment: 'primary',
      supplies: 'success',
      swarms: 'secondary',
      queens: 'secondary',
    }
    return (
      <Chip
        icon={getCategoryIcon(cat)}
        label={getCategoryLabel(cat)}
        color={colorMap[cat] || 'default'}
        size="small"
        variant="outlined"
      />
    )
  }

  // "equipment" (legacy tools items) intentionally has no tab — those rows only appear under "All"
  const tabCategories = ['all', 'boxes', 'honey', 'combs', 'supplies', 'swarms', 'queens']

  return (
    <Container>
      <Header>
        <Title>{t('inventory.title')}</Title>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
          {t('inventory.add')}
        </Button>
      </Header>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* KPI Cards */}
      <KpiGrid>
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#e3f2fd', display: 'flex' }}>
                <InventoryIcon sx={{ color: '#1976d2' }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>{kpis.totalItems}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('inventory.kpi.totalItems')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#e8f5e9', display: 'flex' }}>
                <ViewInArIcon sx={{ color: '#2e7d32' }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>{kpis.totalUnits}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('inventory.kpi.totalUnits')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fff3e0', display: 'flex' }}>
                <WarningAmberIcon sx={{ color: '#ed6c02' }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color={kpis.lowStock > 0 ? 'warning.main' : 'text.primary'}>
                  {kpis.lowStock}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('inventory.kpi.lowStock')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: kpis.critical > 0 ? '#fbe9e7' : '#e8f5e9', display: 'flex' }}>
                {kpis.critical > 0
                  ? <WarningAmberIcon sx={{ color: '#d32f2f' }} />
                  : <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                }
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color={kpis.critical > 0 ? 'error.main' : 'success.main'}>
                  {kpis.critical > 0 ? kpis.critical : t('inventory.kpi.allGood')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {kpis.critical > 0
                    ? t('inventory.kpi.critical')
                    : t('inventory.kpi.stockHealth')
                  }
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </KpiGrid>

      {/* Category Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabCategories.map(cat => (
            <Tab
              key={cat}
              value={cat}
              sx={{ textTransform: 'none' }}
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  {cat !== 'all' && getCategoryIcon(cat)}
                  <span>{cat === 'all' ? t('inventory.all') : getCategoryLabel(cat)}</span>
                  <Chip label={categoryCounts[cat] || 0} size="small" sx={{ ml: 0.5, height: 20, fontSize: 12 }} />
                </Box>
              }
            />
          ))}
        </Tabs>

        {/* Filters */}
        <Box sx={{ p: 2 }}>
          <FilterBar>
            <TextField
              size="small"
              placeholder={t('form.placeholder.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
              sx={{ minWidth: 250 }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('inventory.filters.location')}</InputLabel>
              <MuiSelect
                value={locationFilter}
                onChange={e => setLocationFilter(e.target.value)}
                label={t('inventory.filters.location')}
              >
                <MenuItem value="all">{t('inventory.filters.allLocations')}</MenuItem>
                {locations.filter(l => l.type === 'warehouse').length > 0 && (
                  <MenuItem disabled sx={{ opacity: 0.6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                    {t('inventory.warehouses')}
                  </MenuItem>
                )}
                {locations.filter(l => l.type === 'warehouse').map(l => (
                  <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                ))}
                {locations.filter(l => l.type === 'yard').length > 0 && (
                  <MenuItem disabled sx={{ opacity: 0.6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                    {t('yard.list')}
                  </MenuItem>
                )}
                {locations.filter(l => l.type === 'yard').map(l => (
                  <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                ))}
              </MuiSelect>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {t('inventory.itemCount', { count: filteredItems.length })}
            </Typography>
          </FilterBar>
        </Box>

        {/* Table */}
        <TableContainer sx={{ border: 0, borderRadius: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel active={sortKey === 'type'} direction={sortKey === 'type' ? sortDir : 'asc'} onClick={() => handleSort('type')}>
                    {t('form.label.name')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t('inventory.category')}</TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortKey === 'quantity'} direction={sortKey === 'quantity' ? sortDir : 'asc'} onClick={() => handleSort('quantity')}>
                    {t('form.label.quantity')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortKey === 'location'} direction={sortKey === 'location' ? sortDir : 'asc'} onClick={() => handleSort('location')}>
                    {t('inventory.filters.location')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortKey === 'status'} direction={sortKey === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>
                    {t('users.status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <InventoryIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography color="text.secondary">
                      {t('common.notFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map(item => (
                  <TableRow
                    key={item._id || item.id}
                    hover
                    sx={{
                      borderLeft: getStockStatus(item) === 'critical' ? '3px solid #d32f2f'
                        : getStockStatus(item) === 'low' ? '3px solid #ed6c02'
                        : '3px solid transparent',
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {/* bdi: keep Latin names like "4-way pallets" in order inside RTL rows */}
                        <bdi>{getItemName(item)}</bdi>
                      </Typography>
                      {item.description && getItemCategory(item) === 'boxes' && (
                        <Typography variant="caption" color="text.secondary"><bdi>{item.description}</bdi></Typography>
                      )}
                    </TableCell>
                    <TableCell>{renderCategoryChip(item)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2" fontWeight={700} sx={{
                        color: getStockStatus(item) === 'critical' ? '#d32f2f'
                          : getStockStatus(item) === 'low' ? '#ed6c02'
                          : 'inherit'
                      }}>
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderLocationChip(item)}</TableCell>
                    <TableCell>{renderStatusChip(item)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('inventory.transfer')}>
                        <IconButton size="small" color="info" onClick={() => handleTransferClick(item)}>
                          <SwapHorizIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('button.edit.text')}>
                        <IconButton size="small" color="primary" onClick={() => handleEditClick(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('button.delete.text')}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? t('inventory.editItem') : t('inventory.addItem')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>{t('form.label.type')}</InputLabel>
              <MuiSelect
                value={form.type}
                onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                label={t('form.label.type')}
              >
                <MenuItem value="boxes">{t('inventory.boxes')}</MenuItem>
                <MenuItem value="honey">{t('inventory.honey')}</MenuItem>
                <MenuItem value="swarms">{t('inventory.swarms')}</MenuItem>
                <MenuItem value="queens">{t('inventory.queens')}</MenuItem>
                {/* "Tools" is no longer offered for new items; keep it only so editing a legacy tools item shows its current type */}
                {editingItem?.type === 'tools' && (
                  <MenuItem value="tools">{t('inventory.tools')}</MenuItem>
                )}
              </MuiSelect>
            </FormControl>

            {form.type === 'boxes' && (
              <FormControl fullWidth>
                <InputLabel>{t('form.label.boxType')}</InputLabel>
                <MuiSelect
                  value={form.boxType}
                  onChange={e => setForm(prev => ({ ...prev, boxType: e.target.value }))}
                  label={t('form.label.boxType')}
                >
                  <MenuItem value="deeps">{t('inventory.boxTypes.deeps')}</MenuItem>
                  <MenuItem value="super">{t('inventory.boxTypes.super')}</MenuItem>
                  <MenuItem value="shallow">{t('inventory.boxTypes.shallow')}</MenuItem>
                </MuiSelect>
              </FormControl>
            )}

            {form.type === 'honey' && (
              <FormControl fullWidth>
                <InputLabel>{t('inventory.honeyFormat')}</InputLabel>
                <MuiSelect
                  value={form.format}
                  onChange={e => setForm(prev => ({ ...prev, format: e.target.value }))}
                  label={t('inventory.honeyFormat')}
                >
                  <MenuItem value="barrel">{t('inventory.barrel')}</MenuItem>
                  <MenuItem value="combHoney">{t('inventory.combHoney')}</MenuItem>
                </MuiSelect>
              </FormControl>
            )}

            <TextField
              label={t('form.label.quantity')}
              type="number"
              value={form.quantity}
              onChange={e => setForm(prev => ({ ...prev, quantity: Number(e.target.value) }))}
              fullWidth
              inputProps={{ min: 0 }}
            />

            <TextField
              label={t('form.label.notes')}
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />

            <FormControl fullWidth>
              <InputLabel>{t('inventory.filters.location')}</InputLabel>
              <MuiSelect
                value={form.warehouseId ? `w-${form.warehouseId}` : form.yardId ? `y-${form.yardId}` : ''}
                onChange={e => {
                  const val = e.target.value as string
                  if (val.startsWith('w-')) {
                    setForm(prev => ({ ...prev, warehouseId: val.slice(2), yardId: '' }))
                  } else if (val.startsWith('y-')) {
                    setForm(prev => ({ ...prev, yardId: val.slice(2), warehouseId: '' }))
                  } else {
                    setForm(prev => ({ ...prev, warehouseId: '', yardId: '' }))
                  }
                }}
                label={t('inventory.filters.location')}
              >
                <MenuItem value="">{t('common.select')}</MenuItem>
                {locations.filter(l => l.type === 'warehouse').map(l => (
                  <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                ))}
                {yards.map(y => {
                  const yId = String(y.id || y._id)
                  return <MenuItem key={`y-${yId}`} value={`y-${yId}`}>{y.name}</MenuItem>
                })}
              </MuiSelect>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t('button.cancel.text')}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || form.quantity < 0}>
            {saving ? t('button.save.loading') : t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('button.delete.text')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('inventory.confirmDelete')}
          </Typography>
          {editingItem && (
            <Box mt={2} p={2} sx={{ bgcolor: '#fafafa', borderRadius: 1 }}>
              <Typography variant="subtitle2">{getItemName(editingItem)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.quantityValue', { quantity: editingItem.quantity })} &bull; {getLocationName(editingItem)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            {t('button.cancel.text')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={saving}>
            {saving ? t('common.loading') : t('button.delete.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('inventory.transferTitle')}</DialogTitle>
        <DialogContent>
          {transferItem && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              {/* Source info */}
              <Box p={2} sx={{ bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('inventory.transferFrom')}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {getItemName(transferItem)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getLocationName(transferItem)} &bull; {t('inventory.quantityValue', { quantity: transferItem.quantity })}
                </Typography>
              </Box>

              {/* Quantity */}
              <TextField
                label={t('form.label.quantity')}
                type="number"
                value={transferForm.quantity}
                onChange={e => setTransferForm(prev => ({ ...prev, quantity: Math.min(Number(e.target.value), transferItem.quantity) }))}
                fullWidth
                inputProps={{ min: 1, max: transferItem.quantity }}
                helperText={t('inventory.availableQuantity', { quantity: transferItem.quantity })}
              />

              {/* Destination */}
              <FormControl fullWidth>
                <InputLabel>{t('inventory.transferTo')}</InputLabel>
                <MuiSelect
                  value={transferForm.destination}
                  onChange={e => setTransferForm(prev => ({ ...prev, destination: e.target.value }))}
                  label={t('inventory.transferTo')}
                >
                  {allDestinations.filter(d => d.type === 'warehouse').length > 0 && (
                    <MenuItem disabled sx={{ opacity: 0.6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                      {t('inventory.warehouses')}
                    </MenuItem>
                  )}
                  {allDestinations
                    .filter(d => d.type === 'warehouse')
                    .filter(d => {
                      // Don't show current location as destination
                      if (transferItem.warehouseId && d.id === `w-${transferItem.warehouseId}`) return false
                      return true
                    })
                    .map(d => (
                      <MenuItem key={d.id} value={d.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <WarehouseIcon fontSize="small" sx={{ color: '#7c4dff' }} />
                          {d.name}
                        </Box>
                      </MenuItem>
                    ))}
                  {allDestinations.filter(d => d.type === 'yard').length > 0 && (
                    <MenuItem disabled sx={{ opacity: 0.6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                      {t('yard.list')}
                    </MenuItem>
                  )}
                  {allDestinations
                    .filter(d => d.type === 'yard')
                    .filter(d => {
                      if (transferItem.yardId && d.id === `y-${transferItem.yardId}`) return false
                      return true
                    })
                    .map(d => (
                      <MenuItem key={d.id} value={d.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <HiveIcon fontSize="small" sx={{ color: '#ff9800' }} />
                          {d.name}
                        </Box>
                      </MenuItem>
                    ))}
                </MuiSelect>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)} disabled={saving}>
            {t('button.cancel.text')}
          </Button>
          <Button
            onClick={handleTransfer}
            variant="contained"
            color="info"
            startIcon={<SwapHorizIcon />}
            disabled={saving || !transferForm.destination || transferForm.quantity <= 0}
          >
            {saving ? t('common.loading') : t('inventory.transfer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default InventoryScreen
